import React from 'react';
import strings from '../../../localization';
import TablePage from '../../../common/TablePage';
import { Grid, TextField } from '@material-ui/core';
import IconButton from "@material-ui/core/IconButton";
import SelectControl from '../../../components/controls/SelectControl';
import DatePickerControl from '../../../components/controls/DatePickerControl';
import { getAllGraveyards } from '../../../services/graveyard/GraveyardService';
import { getYears } from '../../../util/DateUtil';
import { downloadReport, downloadXlsxReport } from '../../../util/ReviewsUtil';

/**
 * Shared screen for the nine reports of specification point 23. Every one of them is
 * "pick a period (or a year), press a button, get a file", so a subclass only declares
 * which filters it needs (getFields), which service call generates the file (getService)
 * and how the downloaded file is named (getFileName).
 */
class SpecificationReportPage extends TablePage {

  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      showAdd: false,
      showSerach: false,
      generating: false,
      filter: {
        years: getYears(),
        graveyards: [],
        renewalTypes: [
          { value: '', name: strings.specificationReports.allRenewalTypes },
          { value: '01', name: strings.specificationReports.renewalTypeGrave },
          { value: '02', name: strings.specificationReports.renewalTypeReservation },
          { value: '03', name: strings.specificationReports.renewalTypeTomb },
          { value: '04', name: strings.specificationReports.renewalTypeCassette },
          { value: '08', name: strings.specificationReports.renewalTypeChild },
        ],
      },
      data: {},
    };
  }

  componentDidMount() {

    this.setState({
      companyId: this.props.auth.user.company.id
    });

    if (!this.hasField('graveyard')) {
      return;
    }

    getAllGraveyards().then(response => {
      if (!response.ok) {
        return;
      }

      this.setState(prevState => ({
        filter: {
          ...prevState.filter,
          graveyards: [{ id: -1, name: strings.specificationReports.allGraveyards }, ...response.data.entities]
        }
      }));
    });
  }

  componentWillReceiveProps(nextProps) {
    strings.setLanguage(nextProps.auth.user.language);
  }

  /** Filters: 'graveyard', 'graveField', 'period', 'yearRange', 'twoYears', 'year', 'yearAsPeriod'. */
  getFields() {
    return [];
  }

  /** The ReportService function that fetches the file. */
  getService() {
    return null;
  }

  /** Name of the downloaded file, without extension. */
  getFileName() {
    return "Izvestaj";
  }

  getPageTitle() {
    return "";
  }

  getPageHeader() {
    return <h1>{this.getPageTitle()}</h1>
  }

  /** Both 'yearRange' and 'twoYears' render the same pair of year selects. */
  hasYearPair() {
    return this.hasField('yearRange') || this.hasField('twoYears');
  }

  hasField(field) {
    return this.getFields().indexOf(field) !== -1;
  }

  /**
   * Instant.parse() on the back end needs a full ISO instant, and the period is inclusive,
   * so the last day has to reach its end rather than its midnight.
   */
  periodBoundaries() {

    let from = new Date(this.state.data.periodFrom);
    from.setHours(0, 0, 0, 0);

    let to = new Date(this.state.data.periodTo);
    to.setHours(23, 59, 59, 999);

    return { from: from.toISOString(), to: to.toISOString() };
  }

  isValid() {

    if (this.hasField('period') && (!this.state.data.periodFrom || !this.state.data.periodTo)) {
      return false;
    }

    if (this.hasField('period') && this.state.data.periodFrom > this.state.data.periodTo) {
      return false;
    }

    if (this.hasYearPair() && (!this.state.data.yearFrom || !this.state.data.yearTo)) {
      return false;
    }

    if (this.hasYearPair() && this.state.data.yearFrom.value > this.state.data.yearTo.value) {
      return false;
    }

    if ((this.hasField('year') || this.hasField('yearAsPeriod')) && !this.state.data.year) {
      return false;
    }

    return true;
  }

  /** companyId stays first: makeParametersList() only url-encodes the first parameter. */
  getRequestData(format) {

    let data = {
      companyId: this.state.companyId,
      userId: this.props.auth.user.id,
    };

    if (this.hasField('graveyard')) {
      data.graveyardId = this.state.data.graveyard ? this.state.data.graveyard.id : -1;
    }

    if (this.hasField('graveField')) {
      data.graveField = this.state.data.graveField ? this.state.data.graveField.trim() : "";
    }

    if (this.hasField('period')) {
      const period = this.periodBoundaries();
      data.periodFrom = period.from;
      data.periodTo = period.to;
    }

    if (this.hasYearPair()) {
      data.yearFrom = this.state.data.yearFrom.value;
      data.yearTo = this.state.data.yearTo.value;
    }

    if (this.hasField('year')) {
      data.year = this.state.data.year.value;
    }

    // one chosen year, sent as that year's full period because the endpoint takes a period
    if (this.hasField('yearAsPeriod')) {
      const year = this.state.data.year.value;
      data.periodFrom = new Date(year, 0, 1, 0, 0, 0, 0).toISOString();
      data.periodTo = new Date(year, 11, 31, 23, 59, 59, 999).toISOString();
    }

    if (this.hasField('renewalType')) {
      data.renewalType = this.state.data.renewalType ? this.state.data.renewalType.value : '';
    }

    data.format = format;

    return data;
  }

  generateReport(format) {

    if (!this.isValid()) {
      return;
    }

    this.setState({ generating: true });

    this.getService()(this.getRequestData(format)).then(response => {

      this.setState({ generating: false });

      if (!response || !response.ok) {
        this.props.enqueueSnackbar(strings.specificationReports.generateError, { variant: 'error' });
        return;
      }

      this.props.enqueueSnackbar(strings.specificationReports.generateSuccess, { variant: 'success' });

      if (format === 'xlsx') {
        downloadXlsxReport(response, this.getFileName());
        return;
      }

      downloadReport(response, this.getFileName());
    });
  }

  renderFilters() {
    return (
      <React.Fragment>
        {this.hasField('graveyard') &&
          <SelectControl
            options={this.state.filter.graveyards}
            selected={this.state.data.graveyard}
            onChange={this.changeData}
            label={strings.specificationReports.graveyard}
            placeholder={strings.selectControl.select}
            name={"graveyard"}
            nameKey={"name"}
            valueKey={"id"}
          />
        }
        {this.hasField('graveField') &&
          <TextField
            label={strings.specificationReports.graveField}
            placeholder={strings.specificationReports.graveFieldPlaceholder}
            name={"graveField"}
            value={this.state.data.graveField ? this.state.data.graveField : ""}
            onChange={this.changeData}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        }
        {this.hasField('period') &&
          <DatePickerControl
            label={strings.specificationReports.periodFrom}
            placeholder={strings.specificationReports.periodFrom}
            name="periodFrom"
            onChange={this.changeData}
            date={this.state.data.periodFrom ? new Date(this.state.data.periodFrom) : this.state.data.periodFrom}
            isClearable={true}
            dateFormat="dd/MM/yyyy"
          />
        }
        {this.hasField('period') &&
          <DatePickerControl
            label={strings.specificationReports.periodTo}
            placeholder={strings.specificationReports.periodTo}
            name="periodTo"
            onChange={this.changeData}
            date={this.state.data.periodTo ? new Date(this.state.data.periodTo) : this.state.data.periodTo}
            isClearable={true}
            dateFormat="dd/MM/yyyy"
          />
        }
        {this.hasYearPair() &&
          <SelectControl
            options={this.state.filter.years}
            selected={this.state.data.yearFrom}
            onChange={this.changeData}
            label={this.hasField('twoYears') ? strings.specificationReports.firstYear : strings.specificationReports.yearFrom}
            placeholder={strings.selectControl.select}
            name={"yearFrom"}
            nameKey={"name"}
            valueKey={"value"}
          />
        }
        {this.hasYearPair() &&
          <SelectControl
            options={this.state.filter.years}
            selected={this.state.data.yearTo}
            onChange={this.changeData}
            label={this.hasField('twoYears') ? strings.specificationReports.secondYear : strings.specificationReports.yearTo}
            placeholder={strings.selectControl.select}
            name={"yearTo"}
            nameKey={"name"}
            valueKey={"value"}
          />
        }
        {this.hasField('renewalType') &&
          <SelectControl
            options={this.state.filter.renewalTypes}
            selected={this.state.data.renewalType}
            onChange={this.changeData}
            label={strings.specificationReports.renewalType}
            placeholder={strings.selectControl.select}
            name={"renewalType"}
            nameKey={"name"}
            valueKey={"value"}
          />
        }
        {(this.hasField('year') || this.hasField('yearAsPeriod')) &&
          <SelectControl
            options={this.state.filter.years}
            selected={this.state.data.year}
            onChange={this.changeData}
            label={strings.specificationReports.year}
            placeholder={strings.selectControl.select}
            name={"year"}
            nameKey={"name"}
            valueKey={"value"}
          />
        }
      </React.Fragment>
    );
  }

  render() {
    return (
      <Grid id='table-page'>
        <div className='header'>
          {this.getPageHeader()}
        </div>
        <div className='review-select'>
          {this.renderFilters()}
        </div>
        <div className="report-button">
          <IconButton onClick={() => this.generateReport('pdf')} disabled={this.state.generating || !this.isValid()}>
            {strings.specificationReports.generatePdf}
          </IconButton>
          <IconButton onClick={() => this.generateReport('xlsx')} disabled={this.state.generating || !this.isValid()}>
            {strings.specificationReports.generateXlsx}
          </IconButton>
        </div>
      </Grid>
    );
  }
}

export default SpecificationReportPage;
